<template>
    <div class="ssml-template-control">
        <div class="ssml-attribute ssml-attribute--format">
            <div class="ssml-attribute__label">{{ getAttributeLabel(attribute) }} format</div>
            <div class="ssml-attribute__input">
                <CustomSelect
                    :model-value="template"
                    :options="getAttributeOptions(attribute)"
                    :form-value-text="item => getAttributeValueLabel(item, attribute)"
                    clearable
                    @update:model-value="changeTemplate"
                >
                    <template #default="{ item }">
                        {{ getAttributeValueLabel(item, attribute) }}
                    </template>
                </CustomSelect>
            </div>
        </div>
        <div v-if="template && tokens" class="ssml-attribute ssml-template-control__value">
            <div class="ssml-attribute__label">
                {{ getAttributeLabel(attribute) }}
            </div>
            <!--  -->
            <div class="ssml-attribute__input">
                <div
                    v-for="(item, token) in tokens"
                    :key="token"
                    :class="[
                        'ssml-template-control__input',
                        {
                            'ssml-template-control__input--single': arrayTokens
                        }
                    ]"
                >
                    <FormInput
                        v-if="item.type === 'input'"
                        :model-value="values[token]"
                        editable
                        clearable
                        :disabled="isParentRequired(item)"
                        theme="editor"
                        width="240px"
                        @update:model-value="setTokenValue(token, $event)"
                    />
                    <CustomSelect
                        v-if="item.type === 'select' || item.type === 'multiselect'"
                        :model-value="values[token]"
                        :options="getAttributeOptions(item)"
                        :form-value-text="item => getAttributeValueLabel(item, item)"
                        :disabled="isParentRequired(item)"
                        :width="''"
                        @update:model-value="setTokenValue(token, $event)"
                    >
                        <template #default="{ item}">
                            {{ getAttributeValueLabel(item, item) }}
                        </template>
                    </CustomSelect>
                    <NumberInput
                        v-if="item.type === 'number'"
                        :model-value="values[token]"
                        :step="item.step || 1"
                        with-input
                        :disabled="isParentRequired(item)"
                        :max="getLimitation(item, 'max')"
                        :min="getLimitation(item, 'min')"
                        @update:model-value="setTokenValue(token, $event)"
                    />
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { FormInput, CustomSelect, NumberInput } from '@speechki-frontend/shared/ui/forms';

import {
    createTemplateRegexp,
    createTokenRegex,
    matchValue,
    buildTemplate,
    typeValue
} from './utils';

export default {
    name: 'SsmlTemplateControl',
    components: {
        FormInput,
        CustomSelect,
        NumberInput
    },
    inject: [
        'getAttributeLabel',
        'getAttributeOptions',
        'getAttributeValueLabel',
        'isParentRequired',
        'getLimitation'
    ],
    props: {
        attribute: {
            type: Object,
            required: true
        },
        value: {
            type: String,
            default: ''
        }
    },

    data() {
        return {
            template: '',
            values: {},
            initial: true
        };
    },

    computed: {
        templates() {
            const { values = [] } = this.attribute;
            return values;
        },

        templatesData() {
            return Object.values(this.templates).reduce((acc, template) => {
                const tokens = this.createTemplateTokens(template);
                const regexp = createTemplateRegexp(template, tokens);

                const data = {
                    template,
                    tokens,
                    regexp
                };

                acc[template] = data;

                return acc;
            }, {});
        },

        tokens() {
            if (!this.template) return {};

            const { tokens = {} } = this.templatesData[this.template];
            return tokens;
        },

        requiredValuesPresent() {
            if (!this.template) return false;

            return Object.entries(this.values).every(([token, value], _, array) => {
                if (!array.length) return false;

                const tokenProps = this.attribute.template_items[token];

                if (tokenProps.type === 'number') {
                    return !Number.isNaN(value);
                }

                return !!value;
            });
        },

        arrayTokens() {
            return Object.keys(this.templatesData[this.template].tokens).length === 1;
        }
    },

    created() {
        if (this.value) {
            this.valueChanged();
        }
    },

    methods: {
        createTemplateTokens(template) {
            if (!template) return {};

            const tokens = template.match(/\{(.*?)\}/g).map(token => token.replace(/\{|\}/g, ''));

            // sort property is to keep ordering of prefix,values..,suffix
            return tokens.reduce((acc, token, index) => {
                acc[token] = {
                    ...this.attribute.template_items[token],
                    sort: index + 1,
                    regexp: createTokenRegex(this.attribute.template_items[token])
                };

                return acc;
            }, {});
        },

        defineTemplate() {
            if (!this.templatesData || !this.value) return undefined;

            const { template = '' } = Object.values(this.templatesData).find(template => {
                const match = matchValue(this.value, template);
                const final = buildTemplate(match, template.template);

                return final === this.value;
            });

            return template;
        },

        changeTemplate(template) {
            if (template === this.template) return;

            this.template = template;

            // no template - clear
            if (!template) {
                this.$emit('update', '');
                return;
            }

            // new template - save tokens or clear all but keep template (set default values)
            this.updateTokens();

            this.$emit('update', this.createFinalValue());
        },

        valueChanged() {
            this.template = this.defineTemplate();
            const values = matchValue(this.value, this.templatesData[this.template]);
            this.values = values;
        },

        updateTokens() {
            const values = {};
            Object.entries(this.tokens).forEach(([token, data]) => {
                console.log(data);
                values[token] = typeValue(data.default_value, data);
            });
            this.values = values;
        },

        updateValues() {
            this.values = {};
            Object.entries(this.templatesData[this.template].tokens).forEach(([token, data]) => {
                const value = typeValue('', data);

                this.values[token] = value;
            });
        },

        setTokenValue(token, value) {
            this.values[token] = value;

            if (this.requiredValuesPresent) {
                this.$emit('update', this.createFinalValue());
            } else {
                this.$emit('update', '');
            }
        },

        createFinalValue() {
            let final = this.template;

            Object.entries(this.values).forEach(([key, value]) => {
                final = final.replace(`{${key}}`, value);
            });

            return final;
        }
    }
};
</script>

<style src="./SsmlTemplateControl.scss" lang="scss"></style>
